import React, { useState, useEffect } from 'react';
import { useBulkUpdateReportStatesMutation, useGetReportsQuery, useBulkArchiveReportsMutation } from '../../utils/api/endpoints/reportsApi';
import { CTBReport } from '../../utils/api/endpoints/reports/parseReports';
import { UserRole } from '../../utils/api/endpoints/user/credentials';
import useUserCredentials from '../../utils/hooks/user/useUserCredentials';
import toast from 'react-hot-toast';
import { FiCheck, FiZap, FiShield, FiLayers, FiGlobe, FiEye } from 'react-icons/fi';
import BulkActIcon from '../../assets/icons/bulk-act.svg';
import ConfirmationModal from '../common/ConfirmationModal';
import { ReportState, isReportClosed } from '../../utils/api/endpoints/reports/reports';

interface BulkActionsBarProps {
  reports: CTBReport[];
  selectedReports: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  onRefresh: () => void;
  currentFilters?: any; // Current filters applied to the reports view
}

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  reports,
  selectedReports,
  onSelectionChange,
  onRefresh,
  currentFilters
}) => {
  const { role } = useUserCredentials();
  const [bulkUpdateReportStates, { isLoading: isUpdatingStates }] = useBulkUpdateReportStatesMutation();
  const [bulkArchiveReports, { isLoading: isArchiving }] = useBulkArchiveReportsMutation();
  const [action, setAction] = useState<'requestFix' | 'requestRetest' | 'archive' | 'unarchive' | ''>('');
  const [showAllActionable, setShowAllActionable] = useState(false);
  const [archiveModalConfig, setArchiveModalConfig] = useState<{
    isOpen: boolean;
    warning?: string;
    archiveValue: boolean;
  }>({ isOpen: false, archiveValue: true });

  const isLoading = isUpdatingStates || isArchiving;

  // Check if user can perform bulk actions - BUSINESS AND ADMIN ROLES
  const canPerformBulkActions = role && [
    UserRole.BUSINESS,
    UserRole.BUSINESS_MANAGER,
    UserRole.BUSINESS_ADMINISTRATOR,
    UserRole.DEVELOPER,
    UserRole.ADMIN,
    UserRole.SUB_ADMIN,
    UserRole.ADMIN_MANAGER,
    UserRole.QA
  ].includes(role);

  // Fetch all selectable reports when needed (with a high limit to get all)
  const { data: allSelectableData, isLoading: isLoadingAllReports } = useGetReportsQuery(
    {
      search: currentFilters?.searchQuery,
      limit: 2000, // High limit to get all reports
      page: 1,
      // Convert the filters to the format expected by the API
      severity: currentFilters?.severityCategory ? currentFilters.severityCategory.split(',') : undefined,
      type: currentFilters?.vulnerabilityType || undefined,
      reportState: currentFilters?.reportState || undefined,
      program: currentFilters?.program || undefined,
      includeArchived: currentFilters?.includeArchived,
      includeClosed: currentFilters?.includeClosed,
      pentesterUsername: currentFilters?.pentesterUsername || undefined
    },
    {
      skip: !showAllActionable || !canPerformBulkActions
    }
  );

  const allSelectableReports = allSelectableData?.reports || [];

  // Use all reports when in cross-page mode, otherwise use current page
  const reportsToWorkWith = showAllActionable ? allSelectableReports : reports;
  const totalSelectableCount = showAllActionable ? allSelectableReports.length : reports.length;

  // Get available actions based on selected reports
  const getAvailableActions = () => {
    if (selectedReports.length === 0) return [];

    const combinedReports = [...reports, ...allSelectableReports];
    const uniqueReports = Array.from(new Map(combinedReports.map(item => [item.id, item])).values());
    const selectedReportObjects = uniqueReports.filter(report =>
      selectedReports.includes(report.id)
    );

    const actions = [];

    // Business specific actions
    const isBusinessUser = role && [
      UserRole.BUSINESS,
      UserRole.BUSINESS_MANAGER,
      UserRole.BUSINESS_ADMINISTRATOR,
      UserRole.DEVELOPER
    ].includes(role);

    if (isBusinessUser) {
      // Check if any reports can be marked for fixing (Business Approved -> Awaiting Fix)
      const canRequestFix = selectedReportObjects.some(report =>
        report.state === 'Business Approved'
      );
      if (canRequestFix) {
        actions.push({ key: 'requestFix', label: 'Fixing the Vulnerability', description: 'Change from "Business Approved" to "Awaiting Fix"' });
      }

      const canRequestRetest = selectedReportObjects.some(report =>
        report.state === 'Awaiting Fix'
      );
      if (canRequestRetest) {
        actions.push({ key: 'requestRetest', label: 'Request Retest', description: 'Change from "Awaiting Fix" to "Retest Requested"' });
      }
    }

    // Always allow archive/unarchive for selected reports (Admins and Business)
    actions.push({ key: 'archive', label: 'Archive', description: 'Hide selected reports from regular view' });
    actions.push({ key: 'unarchive', label: 'Unarchive', description: 'Show selected reports in regular view' });

    return actions;
  };

  const availableActions = getAvailableActions();

  // Handle select all/none
  const handleSelectAll = () => {
    const allReportIds = reportsToWorkWith.map(report => report.id);
    onSelectionChange(allReportIds);
    toast.success(`Selected all ${allReportIds.length} reports ${showAllActionable ? 'across all pages' : 'on this page'}`);
  };

  const handleSelectNone = () => {
    onSelectionChange([]);
  };

  // Toggle between current page and all pages mode
  const toggleViewMode = () => {
    setShowAllActionable(!showAllActionable);
    // Clear selections when switching modes
    onSelectionChange([]);
  };

  // Handle bulk action
  const handleBulkAction = async () => {
    if (!action || selectedReports.length === 0) return;

    // For archive actions, show a confirmation modal with a warning if needed
    if (action === 'archive' || action === 'unarchive') {
      const isArchiving = action === 'archive';
      const combinedReports = [...reports, ...allSelectableReports];
      const uniqueReports = Array.from(new Map(combinedReports.map(item => [item.id, item])).values());
      const selectedReportObjects = uniqueReports.filter(r => selectedReports.includes(r.id));
      const hasNonClosedReports = isArchiving && selectedReportObjects.some(r => !isReportClosed(r.state));
      const archiveWarning = hasNonClosedReports
        ? `One or more selected reports are not in a Closed state. If they are included in a comprehensive pentest report, that report will be marked as having open findings.\n\nYou can always unarchive these reports later and take action on them to bring them to a Closed state.`
        : undefined;
      setArchiveModalConfig({ isOpen: true, warning: archiveWarning, archiveValue: isArchiving });
      return;
    }

    await executeBulkAction(action, selectedReports);
  };

  // Perform the actual bulk API call
  const executeBulkAction = async (bulkAction: typeof action, reportIds: number[]) => {
    try {
      let result;
      if (bulkAction === 'archive' || bulkAction === 'unarchive') {
        result = await bulkArchiveReports({
          reportIds,
          archive: bulkAction === 'archive'
        }).unwrap();
      } else {
        result = await bulkUpdateReportStates({
          reportIds,
          action: bulkAction as any
        }).unwrap();
      }

      if (result.success) {
        const data = result.data as any;
        const totalUpdated = data.totalUpdated ?? data.updatedCount ?? reportIds.length;
        const totalErrors = data.totalErrors ?? 0;
        const totalSkipped = data.totalSkipped ?? 0;

        let message = `Successfully updated ${totalUpdated} reports`;
        if (totalErrors > 0) message += `. ${totalErrors} errors occurred`;
        if (totalSkipped > 0) message += `. ${totalSkipped} reports were skipped`;

        toast.success(message);

        // Reset form and selection
        setAction('');
        onSelectionChange([]);
        onRefresh();
      } else {
        toast.error(result.message || 'Failed to update reports');
      }
    } catch (error) {
      console.error('Bulk action error:', error);
      toast.error('An error occurred while performing bulk action');
    }
  };

  // Reset form when selection changes
  useEffect(() => {
    if (selectedReports.length === 0) {
      setAction('');
    }
  }, [selectedReports]);

  if (!canPerformBulkActions) {
    return null;
  }

  if (reports.length === 0 && !showAllActionable) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
        <div className="text-center text-gray-500 text-sm">
          No reports available for bulk actions
        </div>
      </div>
    );
  }

  const selectedCount = selectedReports.length;

  return (
    <div className="border border-gray-200 rounded-lg shadow-md overflow-hidden bg-[#F8FAFC]">
      {/* Mode Toggle Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
            <FiLayers className="h-4 w-4" />
          </div>
          <div>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Selection Mode</span>
            <div className="flex items-center gap-2 mt-0.5">
              <button
                onClick={() => !showAllActionable ? null : toggleViewMode()}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-all duration-300 ${
                  !showAllActionable
                    ? 'bg-[#010D2C] text-[#58CC02] shadow-sm'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                Current Page
              </button>
              <button
                onClick={() => showAllActionable ? null : toggleViewMode()}
                className={`px-3 py-1 text-xs font-bold rounded-full transition-all duration-300 ${
                  showAllActionable
                    ? 'bg-[#010D2C] text-[#58CC02] shadow-sm'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                All Pages
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
            {showAllActionable ? <FiGlobe className="w-3.5 h-3.5 text-blue-500" /> : <FiEye className="w-3.5 h-3.5 text-orange-500" />}
            <span>{showAllActionable ? 'Cross-page active' : 'Single page active'}</span>
          </div>
          {showAllActionable && isLoadingAllReports && (
            <span className="text-[10px] text-blue-500 animate-pulse mt-0.5">Loading all reports...</span>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between gap-6">
          {/* Selection Summary */}
          <div className="flex items-center gap-6">
            {/* Selection Count Display */}
            <div className="flex items-center gap-3 bg-white p-2 rounded-xl border border-gray-100 shadow-sm min-w-[140px]">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300" style={{ backgroundColor: '#010D2C' }}>
                <span className="text-lg font-black" style={{ color: '#58CC02' }}>{selectedCount}</span>
              </div>
              <div>
                <span className="text-sm font-bold text-gray-900 leading-tight block">
                  {selectedCount} Selected
                </span>
                <span className="text-[11px] text-gray-500 font-medium">
                  of {totalSelectableCount} {showAllActionable ? 'total' : 'on page'}
                </span>
              </div>
            </div>

            {/* Quick Select Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={handleSelectAll}
                disabled={selectedCount === totalSelectableCount && totalSelectableCount > 0}
                className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-200 border ${
                  selectedCount === totalSelectableCount && totalSelectableCount > 0
                    ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:text-blue-600 shadow-sm'
                }`}
              >
                Select All {showAllActionable ? 'Filtered' : ''}
              </button>
              {selectedCount > 0 && (
                <button
                  onClick={handleSelectNone}
                  className="px-4 py-2 text-xs font-bold rounded-lg bg-white text-red-500 border border-red-100 hover:bg-red-50 transition-all duration-200 shadow-sm"
                >
                  Clear Selection
                </button>
              )}
            </div>

            {selectedCount === 0 && (
              <div className="hidden md:flex items-center gap-3 bg-blue-50 border border-blue-100 px-4 py-2 rounded-xl">
                <FiLayers className="w-4 h-4 text-blue-500" />
                <div>
                  <p className="text-xs font-bold text-blue-900">Start by selecting reports</p>
                  <p className="text-[10px] text-blue-700">Choose reports individually or use "Select All"</p>
                </div>
              </div>
            )}
          </div>

        {/* Action Controls */}
        {selectedCount > 0 && (
          <div className="flex items-center gap-3">
            {/* Action Buttons */}
            <div className="flex items-center bg-white rounded-xl p-1 border border-gray-200 shadow-sm">
              {availableActions.map((actionOption) => (
                <button
                  key={actionOption.key}
                  onClick={() => setAction(action === actionOption.key ? '' : actionOption.key as any)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg transition-all duration-300 flex items-center gap-2 ${
                    action === actionOption.key
                      ? 'shadow-inner'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  style={action === actionOption.key ? { backgroundColor: '#010D2C', color: '#58CC02' } : {}}
                >
                  {actionOption.key === 'requestFix' && <FiZap className="w-3.5 h-3.5" />}
                  {actionOption.key === 'requestRetest' && <FiLayers className="w-3.5 h-3.5" />}
                  {actionOption.key === 'archive' && <FiShield className="w-3.5 h-3.5" />}
                  {actionOption.key === 'unarchive' && <FiShield className="w-3.5 h-3.5" />}
                  {actionOption.label}
                </button>
              ))}
            </div>

            {/* Execute Button */}
            {action && (
              <button
                onClick={handleBulkAction}
                disabled={isLoading}
                className="px-6 py-2.5 text-xs font-black rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                style={{ backgroundColor: '#010D2C', color: '#58CC02' }}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2" style={{ borderColor: '#58CC02' }}></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <FiCheck className="w-4 h-4" />
                    <span>Execute Action</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
        </div>

        {/* Compact Action Info */}
        {action && availableActions.find(a => a.key === action) && (
          <div className="mt-4 p-3 bg-blue-600 bg-opacity-5 border border-blue-100 rounded-xl animate-in slide-in-from-top-2 duration-300">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
              <div>
                <span className="text-xs font-bold text-blue-900 block">
                  Action Summary
                </span>
                <span className="text-xs text-blue-700">
                  {availableActions.find(a => a.key === action)?.description} for {selectedCount} selected reports.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Archive confirmation modal */}
      <ConfirmationModal
        isOpen={archiveModalConfig.isOpen}
        onClose={() => setArchiveModalConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={() => executeBulkAction(archiveModalConfig.archiveValue ? 'archive' : 'unarchive', selectedReports)}
        title={archiveModalConfig.archiveValue ? 'Archive Reports' : 'Unarchive Reports'}
        description={`Are you sure you want to ${archiveModalConfig.archiveValue ? 'archive' : 'unarchive'} ${selectedReports.length} selected report${selectedReports.length !== 1 ? 's' : ''}?`}
        warning={archiveModalConfig.warning}
        confirmText="Yes, proceed"
        cancelText="Cancel"
      />
    </div>
  );
};

export default BulkActionsBar;
